import { RARITY_ORDER, RARITY_HEX, PITY_THRESHOLDS, MYSTERY_BOX_OUTCOMES, PRESTIGE_COLOURS } from "./constants";
import imageData from "./data/imageData";

// ─── COLOUR HELPERS ───────────────────────────────────────────
export function rarityHex(r) { return RARITY_HEX[r] || "#fff"; }

export function prestigeColour(level) {
  return PRESTIGE_COLOURS[Math.min(level, PRESTIGE_COLOURS.length - 1)] || "";
}

// ─── COLLECTION HELPERS ───────────────────────────────────────
export function getRarest(inv) {
  if (!inv.length) return "—";
  return inv.reduce(
    (b, i) => RARITY_ORDER.indexOf(i.rarity) > RARITY_ORDER.indexOf(b) ? i.rarity : b,
    inv[0].rarity
  );
}

export function pickFromRarity(rarity) {
  const pool = imageData.filter(i => i.rarity === rarity);
  return pool.length ? { ...pool[Math.floor(Math.random() * pool.length)] } : null;
}

export function pickMysteryBox() {
  const total = MYSTERY_BOX_OUTCOMES.reduce((a, o) => a + o.weight, 0);
  const rand  = Math.random() * total;
  let cum = 0;
  for (const o of MYSTERY_BOX_OUTCOMES) {
    cum += o.weight;
    if (rand <= cum) return o;
  }
  return MYSTERY_BOX_OUTCOMES[0];
}

// ─── PITY ─────────────────────────────────────────────────────
export function updatePity(pity, rarity) {
  const idx     = RARITY_ORDER.indexOf(rarity);
  const newPity = { ...pity };
  Object.keys(newPity).forEach(tier => { newPity[tier]++; });
  Object.keys(PITY_THRESHOLDS).forEach(tier => {
    if (idx >= RARITY_ORDER.indexOf(tier)) newPity[tier] = 0;
  });
  return newPity;
}

// ─── ROLL LOGIC ───────────────────────────────────────────────
export function rollOne(boosts, inventory, pity, prestigeLevel) {
  const { luckMult } = getPrestigeBonus(prestigeLevel);

  // Pity check
  for (const tier of ["Legendary", "Epic", "Rare"]) {
    if (pity[tier] >= PITY_THRESHOLDS[tier]) {
      const guaranteed = pickFromRarity(tier);
      if (guaranteed) { guaranteed.rarityClass = guaranteed.rarity.toLowerCase(); return guaranteed; }
    }
  }

  const boostedData = imageData.map(item => ({
    ...item,
    chance: item.rarity === "Common" ? item.chance : item.chance * luckMult,
  }));
  const total = boostedData.reduce((a, i) => a + i.chance, 0);
  const rand  = Math.random() * total;
  let cum = 0, selected = null;
  for (let i = 0; i < boostedData.length; i++) {
    cum += boostedData[i].chance;
    if (rand <= cum) { selected = { ...imageData[i] }; break; }
  }
  if (!selected) return null;

  const idx = RARITY_ORDER.indexOf(selected.rarity);
  if      (boosts.jackpot)         selected = pickFromRarity(RARITY_ORDER[Math.max(idx, 4)]) || selected;
  else if (boosts.epic_guarantee)  selected = pickFromRarity(RARITY_ORDER[Math.max(idx, 3)]) || selected;
  else if (boosts.rare_surge)      selected = pickFromRarity(RARITY_ORDER[Math.max(idx, 2)]) || selected;
  else if (boosts.hot_streak > 0)  selected = pickFromRarity(RARITY_ORDER[Math.min(idx + 1, RARITY_ORDER.length - 1)]) || selected;
  else if (boosts.lucky_charm && selected.rarity === "Common") selected = pickFromRarity("Uncommon") || selected;

  if (boosts.hamster_magnet > 0) {
    const unowned = imageData.filter(i => !inventory.some(inv => inv.name === i.name));
    if (unowned.length) selected = { ...unowned[Math.floor(Math.random() * unowned.length)] };
  }

  selected.rarityClass = selected.rarity.toLowerCase();
  return selected;
}

// ─── PRESTIGE ─────────────────────────────────────────────────
export function getPrestigeBonus(level) {
  return {
    luckMult:   1 + level * 0.05,
    coinMult:   1 + level * 0.10,
    startCoins: level * 50,
  };
}

// ─── TIME HELPERS ─────────────────────────────────────────────
export function timeAgo(ts) {
  const diff = Math.floor((Date.now() - ts) / 1000);
  if (diff < 60)    return `${diff}s ago`;
  if (diff < 3600)  return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export function msUntilMidnight() {
  const now      = new Date();
  const midnight = new Date(now);
  midnight.setHours(24, 0, 0, 0);
  return midnight - now;
}

export function formatCountdown(ms) {
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}
